import React, { useState, useEffect } from "react";
import { Table, Form, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const EmployeeSalary = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({
    user_type: "",
    user_fullname: "",
    user_email: "",
    user_mob: "",
    user_code: "",
    user_designation: "",
    join_date: "",
    left_date: "",
    suspension_date: "",
    employmentstatus: "",
    status: "",
  });
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const itemsPerPage = 100;

  const fetchEmployees = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add API-supported filters
      if (currentFilters.user_type)
        params.append("user_type", currentFilters.user_type);
      if (currentFilters.user_fullname)
        params.append("user_fullname", currentFilters.user_fullname);
      if (currentFilters.user_email)
        params.append("user_email", currentFilters.user_email);
      if (currentFilters.user_mob)
        params.append("user_mob", currentFilters.user_mob);
      if (currentFilters.join_date)
        params.append("join_date", currentFilters.join_date);
      if (currentFilters.left_date)
        params.append("left_date", currentFilters.left_date);
      if (currentFilters.suspension_date)
        params.append("suspension_date", currentFilters.suspension_date);
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_URL}payroll/employee/get-employees?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Response:", response);
      const data = await response.json();

      if (data.status === 1) {
        const transformedData = data.data.map((emp) => ({
          id: emp.userid || emp.id,
          user_fullname: emp.user_fullname,
          user_email: emp.user_email,
          user_mob: emp.user_mob,
          user_code: emp.user_code,
          user_designation: emp.role_name || "N/A",
          join_date: emp.join_date
            ? new Date(emp.join_date).toLocaleDateString()
            : "N/A",
          left_date: emp.left_date
            ? new Date(emp.left_date).toLocaleDateString()
            : "N/A",
          suspension_date: emp.suspension_date
            ? new Date(emp.suspension_date).toLocaleDateString()
            : "N/A",
          employmentstatus: emp.left_date ? "Inactive" : "Active",
          user_role: emp.role_name,
          status: emp.status === 1 ? "Activated" : "Deactivated",
          salary: 0,
          // Add raw dates for filtering
          join_date_raw: emp.join_date,
          left_date_raw: emp.left_date,
          suspension_date_raw: emp.suspension_date,
        }));

        setEmployees(transformedData);
        // Apply client-side filters for fields not supported by API
        applyClientSideFilters(transformedData, currentFilters);
        setRoles(data.roles || []);
        setTotalRecords(data.total);
        setPageCount(Math.ceil(data.total / itemsPerPage));
      } else {
        setEmployees([]);
        setFilteredEmployees([]);
      }
    } catch (error) {
      console.error("Error:", error);
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const applyClientSideFilters = (data, currentFilters) => {
    let filtered = data;

    // Apply client-side filters for fields not handled by API
    if (currentFilters.user_code) {
      filtered = filtered.filter((emp) =>
        emp.user_code
          .toLowerCase()
          .includes(currentFilters.user_code.toLowerCase())
      );
    }

    if (currentFilters.user_designation) {
      filtered = filtered.filter((emp) =>
        emp.user_designation
          .toLowerCase()
          .includes(currentFilters.user_designation.toLowerCase())
      );
    }

    // Date filtering
    if (currentFilters.join_date) {
      const filterDate = new Date(currentFilters.join_date);
      filtered = filtered.filter((emp) => {
        if (!emp.join_date_raw) return false;
        const empDate = new Date(emp.join_date_raw);
        return empDate.toDateString() === filterDate.toDateString();
      });
    }

    if (currentFilters.left_date) {
      const filterDate = new Date(currentFilters.left_date);
      filtered = filtered.filter((emp) => {
        if (!emp.left_date_raw) return false;
        const empDate = new Date(emp.left_date_raw);
        return empDate.toDateString() === filterDate.toDateString();
      });
    }

    if (currentFilters.suspension_date) {
      const filterDate = new Date(currentFilters.suspension_date);
      filtered = filtered.filter((emp) => {
        if (!emp.suspension_date_raw) return false;
        const empDate = new Date(emp.suspension_date_raw);
        return empDate.toDateString() === filterDate.toDateString();
      });
    }

    if (currentFilters.employmentstatus) {
      filtered = filtered.filter(
        (emp) => emp.employmentstatus === currentFilters.employmentstatus
      );
    }

    if (currentFilters.status) {
      filtered = filtered.filter((emp) => emp.status === currentFilters.status);
    }

    setFilteredEmployees(filtered);
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentPage]);

  const handleFilterChange = (name, value) => {
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    // For API-supported filters, reset page and fetch new data
    if (
      ["user_type", "user_fullname", "user_email", "user_mob", "join_date", "left_date", "suspension_date"].includes(name)
    ) {
      setCurrentPage(1);
      fetchEmployees(updatedFilters);
    } else {
      // For client-side filters, just apply filtering
      setIsFiltering(true);
      applyClientSideFilters(employees, updatedFilters);
      setTimeout(() => setIsFiltering(false), 300);
    }
  };

  const handleDateFilterChange = (name, date) => {
    if (!date) {
      handleFilterChange(name, "");
      return;
    }
    // Format as YYYY-MM-DD using local time to avoid UTC shift issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    handleFilterChange(name, dateString);
  };

  const clearFilters = () => {
    const resetFilters = {
      user_type: "",
      user_fullname: "",
      user_email: "",
      user_mob: "",
      user_code: "",
      user_designation: "",
      join_date: "",
      left_date: "",
      suspension_date: "",
      employmentstatus: "",
      status: "",
    };
    setFilters(resetFilters);
    setCurrentPage(1);
    fetchEmployees(resetFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setFilters({
        user_type: "",
        user_fullname: "",
        user_email: "",
        user_mob: "",
        user_code: "",
        user_designation: "",
        join_date: "",
        left_date: "",
        suspension_date: "",
        employmentstatus: "",
        status: "",
      });
      setCurrentPage(1);
      fetchEmployees({
        user_type: "",
        user_fullname: "",
        user_email: "",
        user_mob: "",
        user_code: "",
        user_designation: "",
        join_date: "",
        left_date: "",
        suspension_date: "",
        employmentstatus: "",
        status: "",
      });
    };
    window.addEventListener('clearEmployeeSalaryFiltersEvent', handleClearFilters);
    return () => {
      window.removeEventListener('clearEmployeeSalaryFiltersEvent', handleClearFilters);
    };
  }, []);

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <div className="mt-5">
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title pull-left">Employee Salary</h4>
          </div>
          <div className="me-3 ">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {totalRecords}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {/* Add CSS for sticky headers */}
        <style>
          {`
            .sticky-employee-table thead tr:first-child th {
              position: sticky;
              top: 0;
              z-index: 20;
              background: #f8f9fa;
            }
            
            .sticky-employee-table thead tr:nth-child(2) th {
              position: sticky;
              top: 30px; /* Adjust based on your first header row height */
              z-index: 19;
              background: white;
            }
            
            .sticky-employee-container {
              max-height: calc(100vh - 165px);
              overflow: auto;
            }
            
            .sticky-employee-table th {
              vertical-align: top;
            }

            .skeleton-box {
              height: 15px;
              background: #f2f2f2;
              background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%);
              background-size: 200% 100%;
              animation: skeleton-loading 1.5s infinite;
              border-radius: 4px;
              width: 100%;
            }

            @keyframes skeleton-loading {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>

        <div className="panel-body">
          <div className="sticky-employee-container">
            <Table striped bordered hover className="sticky-employee-table">
              <thead>
                {/* First Header Row - Column Titles */}
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>User Code</th>
                  <th>Designation</th>
                  <th>Join Date</th>
                  <th>Left Date</th>
                  <th>Suspension</th>
                  <th>Employment</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>

                {/* Second Header Row - Search Filters */}
                <tr>
                  <th></th>
                  <th>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search Name"
                      value={filters.user_fullname}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          user_fullname: e.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFilterChange("user_fullname", e.target.value);
                        }
                      }}
                    />
                  </th>
                  <th>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search Email"
                      value={filters.user_email}
                      onChange={(e) =>
                        setFilters({ ...filters, user_email: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFilterChange("user_email", e.target.value);
                        }
                      }}
                    />
                  </th>
                  <th>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search Mobile"
                      value={filters.user_mob}
                      onChange={(e) =>
                        setFilters({ ...filters, user_mob: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFilterChange("user_mob", e.target.value);
                        }
                      }}
                    />
                  </th>
                  <th>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search Code"
                      value={filters.user_code}
                      onChange={(e) =>
                        setFilters({ ...filters, user_code: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFilterChange("user_code", e.target.value);
                        }
                      }}
                    />
                  </th>
                  <th>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search Designation"
                      value={filters.user_designation}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          user_designation: e.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFilterChange(
                            "user_designation",
                            e.target.value
                          );
                        }
                      }}
                    />
                  </th>
                  <th>
                    <DatePicker
                      selected={
                        filters.join_date ? new Date(filters.join_date) : null
                      }
                      onChange={(date) =>
                        handleDateFilterChange("join_date", date)
                      }
                      isClearable
                      placeholderText="Select Date"
                      dateFormat="dd/MM/yyyy"
                      className="form-control form-control-sm"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      scrollableYearDropdown
                      yearDropdownItemNumber={15}
                      minDate={new Date(2020, 0, 1)}
                      maxDate={new Date(2030, 11, 31)}
                      portalId="root"
                      popperProps={{ strategy: "fixed" }}
                    />
                  </th>
                  <th>
                    <DatePicker
                      selected={
                        filters.left_date ? new Date(filters.left_date) : null
                      }
                      onChange={(date) =>
                        handleDateFilterChange("left_date", date)
                      }
                      isClearable
                      placeholderText="Select Date"
                      dateFormat="dd/MM/yyyy"
                      className="form-control form-control-sm"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      scrollableYearDropdown
                      yearDropdownItemNumber={15}
                      minDate={new Date(2020, 0, 1)}
                      maxDate={new Date(2030, 11, 31)}
                      portalId="root"
                      popperProps={{ strategy: "fixed" }}
                    />
                  </th>
                  <th>
                    <DatePicker
                      selected={
                        filters.suspension_date ? new Date(filters.suspension_date) : null
                      }
                      onChange={(date) =>
                        handleDateFilterChange("suspension_date", date)
                      }
                      isClearable
                      placeholderText="Select Date"
                      dateFormat="dd/MM/yyyy"
                      className="form-control form-control-sm"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      scrollableYearDropdown
                      yearDropdownItemNumber={15}
                      minDate={new Date(2020, 0, 1)}
                      maxDate={new Date(2030, 11, 31)}
                      portalId="root"
                      popperProps={{ strategy: "fixed" }}
                    />
                  </th>
                  <th>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={filters.employmentstatus}
                      onChange={(e) =>
                        handleFilterChange("employmentstatus", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Control>
                  </th>
                  <th>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={filters.user_type}
                      onChange={(e) =>
                        handleFilterChange("user_type", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.user_role}
                        </option>
                      ))}
                    </Form.Control>
                  </th>
                  <th>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="Activated">Activated</option>
                      <option value="Deactivated">Deactivated</option>
                    </Form.Control>
                  </th>
                  <th>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={clearFilters}
                    >
                      Clear
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || isFiltering ? (
                  Array.from({ length: 15 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 13 }).map((_, col) => (
                        <td key={col}><div className="skeleton-box" /></td>
                      ))}
                    </tr>
                  ))
                ) : filteredEmployees.length > 0 ? (
                  filteredEmployees.map((emp, index) => (
                    <tr key={emp.id}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>
                        <a
                          href={`/profile/${emp.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {emp.user_fullname}
                        </a>
                      </td>
                      <td>{emp.user_email}</td>
                      <td>{emp.user_mob}</td>
                      <td>{emp.user_code}</td>
                      <td>{emp.user_designation}</td>
                      <td>{emp.join_date}</td>
                      <td>{emp.left_date}</td>
                      <td>{emp.suspension_date}</td>
                      <td>{emp.employmentstatus}</td>
                      <td>{emp.user_role}</td>
                      <td>
                        <span
                          className={`badge ${emp.status === "Activated"
                            ? "badge-success"
                            : "badge-danger"
                            }`}
                        >
                          {emp.status}
                        </span>
                      </td>
                      <td>
                        <PermissionWrapper
                          module="payroll_employee"
                          action="edit"
                        >
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() =>
                              navigate(`/payroll/employee/viewSalary/${emp.id}`)
                            }
                            title="View Salary"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </PermissionWrapper>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="text-center">No records found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeSalary;